import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const FAQS = [
  {
    q: "What areas do you cover?",
    a: "We're based in Bromsgrove, Worcestershire and cover Birmingham, Dudley, Redditch, Gloucestershire and Cheltenham.",
  },
  {
    q: "How much will my refurbishment cost?",
    a: "Every home and every job is different, so we don't do one-size-fits-all prices. We'll come and look at the work, then give you a clear, fixed quote — free and with no obligation.",
  },
  {
    q: "How long does the work take?",
    a: "It depends on the scale — repainting a room is days, a full refurbishment is a bigger project. Whatever the job, you'll get a clear schedule with your quote so you know what to expect.",
  },
  {
    q: "Do you work with landlords and estate agents?",
    a: "Yes — alongside homeowners and tenants, we carry out work for multi-home landlords and local and national estate agents, and we handle contracted maintenance for landlords.",
  },
  {
    q: "How do I get started?",
    a: "Call 07792724398, email preecerefurbishments@gmail.com or fill in the quote form below. We'll arrange a time to look at the job and come back to you with a quote.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 bg-[#121212]">
      <div className="max-w-[860px] mx-auto px-6 md:px-12">
        <Reveal className="text-center mb-14">
          <Eyebrow className="mb-4">Common questions</Eyebrow>
          <h2 className="font-serif text-4xl md:text-6xl text-[#f5f5f0] leading-tight">
            Good to <em className="italic text-[#c5832b]">know.</em>
          </h2>
        </Reveal>

        <div className="border-t border-white/10">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="border-b border-white/10">
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-6 text-left py-6 md:py-7 group"
                  >
                    <span className={`font-serif text-xl md:text-2xl transition-colors duration-300 ${isOpen ? "text-[#d9953c]" : "text-[#f5f5f0] group-hover:text-[#d9953c]"}`}>
                      {f.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-colors duration-300 ${isOpen ? "border-[#c5832b] bg-[#c5832b] text-[#121212]" : "border-white/15 text-[#a3a3a3] group-hover:border-[#c5832b] group-hover:text-[#c5832b]"}`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p
                          data-testid={`faq-answer-${i}`}
                          className="pb-7 pr-14 text-[#a3a3a3] text-base md:text-lg leading-relaxed"
                        >
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
